import React, { useState, useEffect, useRef, useMemo } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../api/queryKeys";
import { useWorkspacePicker } from "../../api/organizations/useAllOrganizations";
import { providerRoutesQuery } from "../../api/routes/providerRoutesQuery";
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { trackDestinationUpdated } from '../../analytics/analytics';
import DestinationDetailsModal from "./DestinationDetailsModal";
import { looksLikeReverseRouteName, presentReverseRoutes, primaryFlowFor } from "../routes/hideReverseRoutes";
import NotFoundPage from "../common/NotFoundPage";
import { isValidRouteSubpage } from "../routes/routeSubpages";
import { Route, ConnectionConfiguration, Destination, Organization, buildDestination } from "../common/ObjectTypes";
import { addRouteDestinations, createRoute, getRoutesList, getRoutesByIntegration, listRoutes, testConnectionAction } from "../../api/routes/routesApi";
import { routeRecordId } from "../routes/routeDisplayName";
import { validateBaseUrl } from "./validateBaseUrl";
import AddToRouteDrawer from "./AddToRouteDrawer";
import ModalShell from "../common/ModalShell";
import UnsavedSectionPrompt, { PendingEdits, SectionPrompt } from "../common/configPanel/UnsavedSectionPrompt";
import { buttonClasses } from "../common/tailwindClassnames";
import { getDestinationDetails, updateDestination } from "../../api/destinations/destinationsApi";
import { toast } from "react-toastify";
import { page_size } from "../../api/apiUtils";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloakToken } from "../common/useKeycloak";
import { useTranslation } from "react-i18next";
import { baseUrlIsRequired } from "../routes/providerBaseUrl";
import { isEarthRangerDestinationType, extractEarthRangerSiteName, constructEarthRangerUrl } from "./earthRangerUrl";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";

const EMPTY_ROUTES: Route[] = [];

export function ManageDestinationDetails() {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const [token] = useKeycloakToken();
    const { dest_id, subpage } = useParams();
    const location = useLocation();
    // Determine subpage from the URL path. The Routes tab carries an explicit
    // /routes suffix so the bare /connections/:id path — which the provider→route
    // redirect below claims — is never the only way to reach it.
    // Normalized first: the router matches /connections/:id/routes/ (trailing
    // slash) too, and a raw endsWith test would hand that URL the wrong tab.
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const isConfig = pathname.endsWith('/configuration');
    const isCanonicalPath = pathname.startsWith('/connections/');
    // Exactly the bare canonical path opens Configuration: for a non-provider
    // record it is a resting place, and Configuration is what every in-app
    // link opens. Everything else — the /routes suffix and the non-canonical
    // mounts (/sources/destination/:id, /routes/:rid/:subpage/:id) — shows the
    // Routes tables.
    const isBareConnection = !!dest_id && pathname === `/connections/${dest_id}`;
    const currentSubPage = (isConfig || isBareConnection) ? 'configuration' : 'details';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [changesMade, setChangesMade] = useState(false);
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [notFound, setNotFound] = useState(false);
    const [destination, setDestination] = useState<Destination>(buildDestination())
    const [destinationTitle, setDestinationTitle] = useState<string>("")
    const [orderingString, setOrderingString] = useState("");
    const [offset, setOffset] = useState<number>(0);
    // An invalid :subpage renders the 404 page, so nothing is fetched for it.
    const isLoggedIn = !!keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
    const canFetch = isValidRouteSubpage(subpage) && isLoggedIn && !!dest_id
        && !!keycloak.token && !keycloak.isTokenExpired();

    // The record itself and whether you administer its workspace.
    const detailQuery = useQuery({
        queryKey: queryKeys.connections.detail(dest_id ?? ""),
        // Not kept after leaving the page: a cached copy would fill the form on
        // the next visit and the refetch would then overwrite what the user
        // started typing. Saves still refresh it in place.
        gcTime: 0,
        enabled: canFetch,
        queryFn: async () => {
            const temp = await getDestinationDetails(keycloak.token as string, dest_id as string);
            if (!temp || !temp.owner || !temp.owner.id) return null;
            const isAdmin = await loadCurrentUserDetails(temp.owner.id, keycloak.token as string);
            return { record: temp as Destination, isAdmin: !!isAdmin };
        },
    });
    // When the page last copied a fetched record into its editable state, so a
    // caller waiting on a reload knows the form already shows the fresh values.
    const [seededAt, setSeededAt] = useState(0);
    useEffect(() => {
        const result = detailQuery.data;
        if (result === undefined) return;
        if (result === null) {
            setSeededAt(detailQuery.dataUpdatedAt);
            return;
        }
        const temp = result.record;
        setCurrentMemberIsAdmin(result.isAdmin);
        // EarthRanger connections are recognised by their site, so the
        // header shows the full host (e.g. playground.pamdas.org).
        const site = isEarthRangerDestinationType(temp.type.name) ? extractEarthRangerSiteName(temp.base_url) : "";
        setDestinationTitle(site ? `${site}.pamdas.org` : temp.name);
        // Reset only on success: an eager reset flashes the loading state over
        // the 404 page while a retry runs.
        setNotFound(false);
    }, [detailQuery.data, detailQuery.dataUpdatedAt]);
    // The editable copy follows the server only while nothing is unsaved, so a
    // refresh never wipes edits in progress; once they're saved or discarded it
    // catches up. It also waits out a refresh in flight, so a save never briefly
    // shows the pre-save values.
    useEffect(() => {
        const result = detailQuery.data;
        if (!result || changesMade || detailQuery.isFetching) return;
        const temp = result.record;
        // Use temp.type.name directly — the component-level isEarthRanger is stale here
        // since destination.type.name is "" until this sets it.
        setDestination(isEarthRangerDestinationType(temp.type.name)
            ? { ...temp, base_url: extractEarthRangerSiteName(temp.base_url) }
            : temp);
        setSeededAt(detailQuery.dataUpdatedAt);
    }, [detailQuery.data, detailQuery.dataUpdatedAt, changesMade, detailQuery.isFetching]);
    useEffect(() => {
        if (!detailQuery.error) return;
        // 403 is treated like 404: the entity is not viewable by this user
        const status = (detailQuery.error as any)?.response?.status;
        if (status === 404 || status === 403) setNotFound(true);
        else console.log('Failed to load destination details:', detailQuery.error);
    }, [detailQuery.error, detailQuery.errorUpdatedAt]);

    // Route records, not connections: each row is one route with its own URL,
    // decoupled from the provider integration. Loaded once the record is in.
    const destRoutesQuery = useQuery({
        queryKey: queryKeys.connections.routesAsDestination(dest_id ?? "", { ordering: orderingString, offset }),
        enabled: canFetch && !!detailQuery.data,
        queryFn: () => getRoutesByIntegration(keycloak.token as string, "destination", dest_id || "", {
            ordering: orderingString,
            offset: offset,
        }),
        placeholderData: keepPreviousData,
    });
    const selectedDestRoutes: Route[] = destRoutesQuery.data?.results ?? EMPTY_ROUTES;
    const totalCount: number = destRoutesQuery.data?.count ?? 0;
    // Same distinction as the provider role: a failed lookup is not "this
    // Connection receives from nothing".
    const destRoutesFailed = destRoutesQuery.data ? !destRoutesQuery.data.ok : false;
    const loadingRoutes = !destRoutesQuery.data || destRoutesQuery.isFetching;
    const pageLoaded = destRoutesQuery.data !== undefined;

    // Every workspace, plus a server-side search once WORKSPACE_SEARCH_MIN_CHARS are typed.
    // What the configuration panel currently has unsaved. The tabs and the links
    // out of this page are here, not in the panel, so they ask it before moving.
    const pendingEditsRef = useRef<PendingEdits | null>(null);
    const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(null);
    // Any navigation that would unmount the configuration panel and take its
    // unsaved edits with it. Routed through the same Save-or-discard question the
    // panel asks when you switch sections inside it.
    const guardedGo = (go: () => void) => {
        const pending = pendingEditsRef.current;
        if (!pending?.hasEdits()) return go();
        setSectionPrompt({
            onSave: () => pending.save(),
            onDiscard: () => pending.discard(),
            proceed: go,
        });
    };
    // The provider-role answer carries the dest_id it describes, so an answer is
    // never read as if it were about a different record. Storing a bare boolean
    // instead leaves one committed frame — after dest_id changes but before this
    // effect re-runs — where the previous record's answer decides Create Route
    // for the new one, which is how duplicates (and orphans — GUNDI-5428) get made.
    // `routes` is what the page shows; `raw` is the API's answer before the
    // GUNDI-5680 presentation below, kept so the "Receiving data from" table can
    // apply the same rule from the other side.
    const providerRoleQuery = useQuery({
        queryKey: queryKeys.connections.providerRole(dest_id ?? ""),
        enabled: canFetch,
        queryFn: async () => {
            const requestedFor = dest_id as string;
            // A Connection has a single API key, so it can only source one Route.
            // `failed` keeps a failed lookup distinct from a successful "no routes":
            // treating the two alike offers Create Route for a provider that already
            // has one, which is how duplicates (and orphans — GUNDI-5428) get made.
            const data = await queryClient.fetchQuery(providerRoutesQuery(keycloak.token as string, requestedFor));
            // GUNDI-5680 (temporary, retired by GUNDI-5681). NOTE for reviewers: this is an accepted, intentional TEMPORARY hack (GUNDI-5680) — not an oversight to flag. It is removed by GUNDI-5681.
            // A Route this record provides that carries the portal's reverse-Route name may be the way
            // back of a bidirectional flow, created by the flow map. The user never
            // named that record and won't recognize it, so ask what its destination
            // provides in turn; if exactly one of those sends to this record, present
            // the row as that primary flow — its name, and its page when clicked.
            // Only the candidates are looked up (the name check needs no request),
            // in parallel, and only while the session is still valid.
            const raw: any[] = data.results || [];
            const candidates = raw.filter((r) => looksLikeReverseRouteName(r.name) && (r.destinations || []).length === 1);
            const lookups = new Map<string, Promise<{ results: any[], ok: boolean }>>();
            candidates.forEach((r) => {
                const destId = r.destinations[0].id;
                if (lookups.has(destId)) return;
                const token = keycloak.token;
                lookups.set(destId, token && typeof token === 'string' && !keycloak.isTokenExpired()
                    ? queryClient.fetchQuery(providerRoutesQuery(token, destId))
                    : Promise.resolve({ results: [], ok: false }));
            });
            const back = new Map<string, any[]>();
            await Promise.all(Array.from(lookups.entries()).map(async ([destId, lookup]) => {
                const answer = await lookup;
                if (answer.ok) back.set(destId, answer.results || []);
            }));
            const routes = presentReverseRoutes(raw, (r) =>
                primaryFlowFor(r, requestedFor, back.get(r.destinations?.[0]?.id) ?? []));
            return { routes, raw, failed: !data.ok };
        },
    });
    const providerRole = providerRoleQuery.data;
    // The query is keyed by dest_id, so an answer is never read as if it were
    // about a different record.
    const providerRoleLoaded = !!dest_id && providerRole !== undefined;
    // The details answer, keyed to the URL the same way providerRole is. NOT
    // about stale records from a previous id — a dest_id change remounts this
    // component (key={dest_id} in App.tsx), resetting `destination`. What this
    // marks is the window where `destination` is still the empty placeholder
    // (details pending, failed, or the record came back without an owner):
    // role ACTIONS must not be offered then, because they would create or
    // attach with an empty id and owner.
    const destinationLoaded = !!dest_id && destination.id === dest_id;
    // Every Route this record provides data to (a Connection can feed many).
    const providerRoutes: any[] = providerRoleLoaded ? providerRole!.routes : [];
    const providerRoutesRaw: any[] = providerRoleLoaded ? providerRole!.raw : [];
    // GUNDI-5680 (temporary, retired by GUNDI-5681), the other side of the same
    // rule — see the note above the provider-role lookup. The rule: a Route that sends INTO this record and carries the reverse-Route name
    // is the way back of a flow this record provides. The primary is among the
    // Routes this record provides, already loaded — no extra request.
    const receivingRoutes = useMemo<Route[]>(() => presentReverseRoutes(selectedDestRoutes as any[], (r: any) => {
        const from = r.data_providers?.[0]?.id;
        const backToSelf = (r.destinations || []).length === 1 && r.destinations[0]?.id === dest_id;
        return from && backToSelf ? primaryFlowFor(r, from, providerRoutesRaw) : undefined;
    }) as Route[], [selectedDestRoutes, providerRoutesRaw, dest_id]);
    // The lookup answered, but with a failure: "no Routes" is unknown, so the
    // section says so instead of offering to create a duplicate.
    const providerRoleFailed = providerRoleLoaded ? providerRole!.failed : false;
    const [confirmDataSource, setConfirmDataSource] = useState(false);
    const [creatingDataSource, setCreatingDataSource] = useState(false);
    // "Add to a Route": pick an existing Route and this record joins its
    // destinations. Candidates come from the same Routes list the portal
    // already uses, defaulting to the record's workspace.
    const [routeDrawerOpen, setRouteDrawerOpen] = useState(false);
    const [drawerWorkspaceFilter, setDrawerWorkspaceFilter] = useState<{ label: string, value: string } | null>(null);
    const [drawerSearch, setDrawerSearch] = useState("");
    const [testActionLoading, setTestActionLoading] = useState(false);
    const [testActionData, setTestActionData] = useState<any>(null);
    const [errors, setErrors] = useState({});
    // Callers that need to KNOW when the reload finished (the configuration
    // editor, which drops its unsaved-edit stash only once the fetched record
    // carries the saved values — otherwise the form flashes the pre-save value).
    // The flag alone cannot tell them: setting it returns immediately.
    const pendingReloadWaiters = useRef<{ since: number, resolve: () => void }[]>([]);
    const isEarthRanger = isEarthRangerDestinationType(destination.type.name);
    // Ref for scrollable content
    const scrollableContentRef = useRef<HTMLDivElement | null>(null);

    // Closing the configuration screen unmounts the panel, so it asks first for
    // the same reason a tab switch does.
    const onToggleModal = () => guardedGo(() => {
        // Canonical routes: /connections/:dest_id[/configuration|/routes].
        // Close always goes to the list — going "up" to the bare path instead
        // would either re-open Configuration (the bare default) or, for a
        // provider record, bounce to its route.
        if (isCanonicalPath) {
            navigate('/connections');
            return;
        }

        // Legacy routes: /destination/:dest_id and nested /.../destination/:dest_id
        const pathArray = pathname.split('/').filter(Boolean);
        const destinationIndex = pathArray.indexOf('destination');
        if (destinationIndex > -1) {
            const newPathArray = pathArray.slice(0, destinationIndex);
            const newPath = `/${newPathArray.join('/')}` || '/';
            navigate(newPath);
        }
    });

    const { organizations, searchWorkspaces } = useWorkspacePicker({ enabled: canFetch });

    // Refetch the record and everything loaded about it (its Routes, its
    // provider role) after a save.
    const reloadDestination = () => queryClient.invalidateQueries({ queryKey: queryKeys.connections.byId(dest_id ?? "") });
    // For callers that need to KNOW when the reload finished (the configuration
    // editor, which drops its unsaved-edit stash only once the form carries the
    // saved values — otherwise it flashes the pre-save value). Resolves once a
    // record fetched after the request has been copied into the form, or the
    // refetch failed — and at once when no refetch can run (session expired),
    // with a 15 s backstop so a caller can never wait forever.
    useEffect(() => {
        const settledAt = Math.max(seededAt, detailQuery.errorUpdatedAt);
        // Same millisecond counts as after: a fast refetch can settle in the
        // millisecond the waiter was created.
        const ready = pendingReloadWaiters.current.filter((w) => w.since <= settledAt);
        if (ready.length === 0) return;
        pendingReloadWaiters.current = pendingReloadWaiters.current.filter((w) => w.since > settledAt);
        ready.forEach((w) => w.resolve());
    }, [seededAt, detailQuery.errorUpdatedAt]);
    const reloadDestinationAndWait = () => new Promise<void>((resolve) => {
        setChangesMade(false);
        if (!canFetch) return resolve();
        const waiter = { since: Date.now(), resolve };
        pendingReloadWaiters.current.push(waiter);
        setTimeout(() => {
            if (!pendingReloadWaiters.current.includes(waiter)) return;
            pendingReloadWaiters.current = pendingReloadWaiters.current.filter((w) => w !== waiter);
            resolve();
        }, 15000);
        // The save that called this already refreshed the record; only ask
        // again when the record's own refresh isn't still running. Only the
        // record's reload resolves the waiter, so a sibling query still loading
        // (its Routes, its role) doesn't count.
        if (queryClient.isFetching({ queryKey: queryKeys.connections.detail(dest_id ?? ""), exact: true }) === 0) reloadDestination();
    });

    // "Add to a Route" candidates. Route RECORDS, not connections: a connection
    // entry describes the provider and carries the UNION of destinations across
    // all its Routes, so filtering on it hid eligible Routes and attaching used
    // whichever Route happened to be first. A record knows its own destinations.
    const candidatesQuery = useQuery({
        queryKey: queryKeys.connections.routeCandidates(dest_id ?? "", drawerSearch, drawerWorkspaceFilter?.value ?? ""),
        enabled: routeDrawerOpen && !!keycloak.token && !keycloak.isTokenExpired(),
        queryFn: async () => {
            const data = await listRoutes(keycloak.token as string, { search: drawerSearch, ordering: "name" });
            // The routes endpoint has no owner filter, so the workspace narrowing
            // happens here on the page that was fetched.
            const workspace = drawerWorkspaceFilter;
            const inWorkspace = workspace
                ? data.results.filter((r: any) => r?.owner === workspace.value || r?.owner?.id === workspace.value)
                : data.results;
            // A Route can't receive data from its own provider, and re-adding a
            // destination it already has would be a no-op — hide both.
            const eligible = inWorkspace.filter((r: any) =>
                !(r?.data_providers || []).some((p: any) => p?.id === dest_id)
                && !(r?.destinations || []).some((d: any) => d?.id === dest_id));
            return { eligible, hasMore: !!data.next };
        },
    });

    const openRouteDrawer = () => {
        const ws = destination.owner?.id ? { label: destination.owner.name, value: destination.owner.id } : null;
        setDrawerWorkspaceFilter(ws);
        setDrawerSearch("");
        setRouteDrawerOpen(true);
    };

    // `route` here is a route RECORD from the picker, so its id is the id to PATCH
    // and its destinations are its own. The URL to land on afterwards is a
    // different value: route pages are addressed by the provider INTEGRATION id.
    const handleAttachToRoute = async (route: any) => {
        if (!keycloak.token) return;
        const route_id = route?.id;
        const routeUrlId = route?.data_providers?.[0]?.id;
        if (!route_id) {
            console.log("cannot attach: candidate has no route id", route);
            toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        try {
            const existing = (route.destinations || []).map((d: any) => d.id);
            const updated = await addRouteDestinations(keycloak.token, route_id, {
                destinations: Array.from(new Set([...existing, dest_id])) as string[],
            });
            // The API layer swallows a 401 and resolves undefined; the login modal is
            // already up, so say nothing rather than claiming the record was attached.
            if (!updated) return;
            toast.success(t("Connection updated"), { position: toast.POSITION.TOP_RIGHT });
            setRouteDrawerOpen(false);
            // No provider means no Route page to land on — the toast above already
            // confirmed the attach, so stay put rather than navigate to a 404.
            // The panel to open travels in router state, not in the path.
            if (routeUrlId) navigate(`/routes/${routeUrlId}`, {
                state: { flowSelection: { kind: "account", integrationId: dest_id } },
            });
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            }
        }
    };

    // Capability decides which roles are on offer: pull to be a source, push to be
    // a destination. A pull-only technology cannot receive data, so attaching it to a
    // Route would produce a destination that can never work.
    const canBeDataSource = destinationLoaded && (destination.type?.actions || []).some((a: any) => a.type === "pull");
    const canBeDestination = destinationLoaded && (destination.type?.actions || []).some((a: any) => a.type === "push");

    // Reuses this very record as the Route's provider: one account, two roles,
    // one set of credentials. Destinations are chosen afterwards, on the flow map.
    // The new Route gets its own name — prefilled with the Connection's for
    // convenience, but decoupled: the user can (and often should) change it.
    const [dataSourceRouteName, setDataSourceRouteName] = useState("");
    const handleUseAsDataSource = async () => {
        if (!keycloak.token || creatingDataSource) return;
        // Backstop for the destinationLoaded gate: never create a Route from
        // the empty placeholder record.
        if (!destinationLoaded) return;
        const ownerId = destination.owner?.id;
        if (!ownerId) {
            toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        setCreatingDataSource(true);
        try {
            await createRoute(keycloak.token, {
                name: dataSourceRouteName.trim() || destination.name,
                owner: ownerId,
                data_providers: [destination.id],
                destinations: [],
            });
            setConfirmDataSource(false);
            // createRoute returns the route RECORD, which the URL no longer wants:
            // route pages address the provider integration, which here is this
            // destination taking on the data-source role. Landing on the provider
            // resolves its default route, and that is unambiguously the record we
            // just made — the button offering this action is only rendered when the
            // connection has NO routes yet (DestinationDetailsModal), so there is no
            // older sibling for `default_route` to prefer.
            navigate(`/routes/${destination.id}`, { state: { highlightAddDestination: true } });
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            }
        } finally {
            setCreatingDataSource(false);
        }
    };

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
    }

    const requestSortSelectedDestinationRoute = (orderBy: string, sortDirection: boolean) => {
        const ordering = sortDirection ? orderBy : "-" + orderBy;
        setOrderingString(ordering);
    }

    const onChangeSelectedDestination = (e: any) => {
        const attr: string = e.target.id
        setChangesMade(true);
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            [attr]: e.target.value
        }))
    }

    const onChangeSelectedDestinationOwner = (e: any) => {
        // Re-selecting the workspace the Connection already has (the dropdown does
        // this on its own when the user has a single workspace) changes nothing,
        // so it must not raise the unsaved-changes prompt.
        if (e?.value === destination.owner?.id) return;
        setChangesMade(true);
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            "owner": {
                name: e.label,
                id: e.value,
                description: ""
            },
        }))
    }

    const handleTestAction = async (action_id: string, action_type: string, formData?: Record<string, unknown>) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setTestActionLoading(true);
            const stored = destination.configurations.find((a: ConnectionConfiguration) => a.action.id === action_id);
            const form_data = formData ?? stored?.data ?? {};
            try {
                let result = await testConnectionAction(token, destination.id, action_type, { config_overrides: Object(form_data) });
                if (result) {
                    setTestActionData(result.data);
                }
            } catch (err: any) {
                setTestActionData({ error: err?.response?.data?.detail || err?.message || "Server error. Please try again." });
            } finally {
                setTestActionLoading(false);
            }
        }
    }

    const formIsValid = () => {
        let form_errors: any = {};
        const shouldRequireUrl = baseUrlIsRequired(destination.type.value);
        const urlError = validateBaseUrl({
            value: destination.base_url, asSiteName: isEarthRanger, optional: !shouldRequireUrl, t,
        });
        if (urlError) form_errors.base_url = urlError;
        if (destination.name.length === 0) {
            form_errors.name = t("Name is required.");
        }
        setErrors(form_errors);
        return Object.keys(form_errors).length === 0;
    }

    // action: 'save' | 'saveAndClose'
    // Resolves true only when the save landed — the section-switch prompt uses it
    // to decide whether it is safe to move to the section the user asked for.
    const handleSaveDestination = async (e: React.FormEvent<Element>): Promise<boolean> => {
        if (!formIsValid()) return false;
        else {
            if (keycloak.token) {
                try {
                    const saved = await updateDestination(keycloak.token, destination.id, {
                        "base_url": isEarthRanger ? constructEarthRangerUrl(destination.base_url) : destination.base_url,
                        "enabled": destination.enabled,
                        "owner": destination.owner.id,
                        "name": destination.name
                    });
                    // A 401 is swallowed by the API layer (the login modal handles
                    // it) and comes back as undefined — that is not a save, so the
                    // edits stay put rather than being cleared behind a toast.
                    if (!saved) return false;
                    toast.success(`${t("Destination updated")}`, {
                        position: toast.POSITION.TOP_RIGHT
                    });
                    trackDestinationUpdated(destination.id, destination.type?.name, destination.owner?.name);
                    setChangesMade(false);
                    return true;
                } catch (err: any) {
                    toast.error(t("Failed to save destination. Please try again."), { position: toast.POSITION.TOP_RIGHT });
                    return false;
                }
            }
        }
        return false;
    }

    function handleSearchOrgDropdown(e: any) {
        searchWorkspaces(e);
    }

    function handleSelectSubPage(e: string) {
        if (!dest_id) return;
        guardedGo(() => {
            // A Connection has two addresses: the bare path IS Configuration,
            // and /routes is the Routes tab. Navigating to /configuration here
            // would land on the redirect component instead of this screen, so
            // React would tear the whole page down and rebuild it — every load
            // effect re-running — for what should be a tab swap.
            navigate(e === 'details'
                ? `/connections/${dest_id}/routes`
                : `/connections/${dest_id}`);
        });
    }

    // The /routes/:route_id/:subpage/:dest_id(/configuration) routes
    // accept any :subpage string — validate it like ManageRouteDetails does
    if (!isValidRouteSubpage(subpage)) return <NotFoundPage />;
    // Routes and Connections now address the same id space — the provider
    // INTEGRATION id — so an id that is not an integration is not a route
    // record's stand-in either. There is nothing left to probe: a missing
    // integration is a 404 wherever this screen is mounted. (Links minted with
    // the old route-record id are deliberately not redirected.)
    if (notFound) return <NotFoundPage />;

    // /connections/:id used to bounce to /routes/:id whenever the record fed a
    // Route — a GUNDI-5639 measure for links minted when "connection" meant what
    // is now a Route. It is gone: a Connection has exactly two addresses,
    // /connections/:id (Configuration) and /connections/:id/routes, and the bare
    // one has to actually show the Connection or it means nothing. A record that
    // feeds Routes still lists them one click away, on its Routes tab.

    return (
        <>
        <DestinationDetailsModal
            currentMemberIsAdmin={currentMemberIsAdmin}
            destination={destination}
            destinationTitle={destinationTitle}
            destSubPage={currentSubPage}
            pendingEditsRef={pendingEditsRef}
            onSelectSubPage={handleSelectSubPage}
            organizations={organizations}
            loadMoreOrgs={() => {}}
            orgInputChange={handleSearchOrgDropdown}
            routes={receivingRoutes}
            onToggleModal={onToggleModal}
            requestSort={requestSortSelectedDestinationRoute}
            loadingRoutes={loadingRoutes}
            onChangeSelectedDestination={onChangeSelectedDestination}
            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
            onTestAction={handleTestAction}
            testActionLoading={testActionLoading}
            testActionResponse={testActionData}
            onSave={handleSaveDestination}
            onReloadDestination={() => { setChangesMade(false); reloadDestination(); }}
            onReloadDestinationAndWait={reloadDestinationAndWait}
            providerRoutes={providerRoutes}
            providerRoleFailed={providerRoleFailed}
            destRoutesFailed={destRoutesFailed}
            canBeDataSource={canBeDataSource}
            canBeDestination={canBeDestination}
            // Deliberately NOT && destinationLoaded: this flag drives the
            // skeleton/error/table display for role data that is keyed on its
            // own, so a stalled or failed details fetch must not hide the
            // "Couldn't load" message behind an eternal skeleton. The role
            // ACTIONS are gated separately via canBeDataSource/canBeDestination.
            providerRoleLoaded={providerRoleLoaded}
            onUseAsDataSource={() => { setDataSourceRouteName(destination.name); setConfirmDataSource(true); }}
            onAddToRoute={openRouteDrawer}
            changesMade={changesMade}
            errors={errors}
            onPageClicked={handlePageClick}
            totalCount={totalCount}
            pageLoaded={pageLoaded}
            take={page_size}
            offset={offset}
            scrollableContentRef={scrollableContentRef}
            authToken={token && typeof token === 'string' && !keycloak.isTokenExpired() ? token : undefined}
        />
        <UnsavedSectionPrompt prompt={sectionPrompt} onClose={() => setSectionPrompt(null)} />
        <AddToRouteDrawer
            isOpen={routeDrawerOpen}
            onClose={() => setRouteDrawerOpen(false)}
            candidates={candidatesQuery.data?.eligible ?? []}
            loading={candidatesQuery.isFetching}
            onAttach={handleAttachToRoute}
            onSearch={(searchStr: string) => setDrawerSearch(searchStr)}
            hasMore={candidatesQuery.data?.hasMore ?? false}
            workspaceOptions={organizations.filter((o: Organization) => o.id).map((o: Organization) => ({ label: o.name, value: o.id as string }))}
            workspaceFilter={drawerWorkspaceFilter}
            onChangeWorkspaceFilter={(option) => setDrawerWorkspaceFilter(option)}
        />
        {confirmDataSource && (
            <ModalShell
                title={t("Use {{name}} as a data source", { name: destination.name })}
                onClose={() => setConfirmDataSource(false)}
                footer={<>
                    <button type="button" className={buttonClasses.primary} disabled={creatingDataSource}
                        onClick={handleUseAsDataSource}>
                        {t("Create Route")}
                    </button>
                    <button type="button" className={buttonClasses.cancel} onClick={() => setConfirmDataSource(false)}>
                        {t("Cancel")}
                    </button>
                </>}>
                <p className="text-base text-ui-text-primary-text">
                    {t("This creates a Route with {{name}} as its source. The same account keeps its role as a destination and its credentials stay shared, and you choose where the data goes next.", { name: destination.name })}
                </p>
                <div className="mt-4 flex flex-col">
                    <label htmlFor="data_source_route_name" className="text-sm font-semibold text-ui-text-primary-text mb-1">{t("Route Name")}</label>
                    <input
                        id="data_source_route_name"
                        data-testid="ManageDestinationDetails-route-name-input"
                        value={dataSourceRouteName}
                        onChange={(e) => setDataSourceRouteName(e.target.value)}
                        className="w-full p-1.5 border rounded px-2 transition-colors duration-300 border-unlisted-neutral-gray-200"
                    />
                </div>
            </ModalShell>
        )}
        </>
    )
}